/**
 * Runtime locale bootstrap (Phase M.4).
 *
 * Replaces Angular's build-time `localize: true` config: instead of
 * shipping two separate `/en/` and `/ar/` bundles, we ship one bundle
 * with `$localize` tagged-template placeholders intact, fetch the
 * locale payload from the backend at startup, and call
 * `loadTranslations()` BEFORE `bootstrapApplication()`.
 *
 * The fetch is non-blocking on failure — if the endpoint is
 * unavailable, the app boots in source-language (English) rather than
 * blocking forever.
 *
 * Priority for which locale to load (highest first):
 *   1. `?locale=ar` URL parameter — debug + share-a-link override
 *   2. `locale` cookie — synced from `User.preferred_language` in M.5
 *   3. `navigator.language` — browser preference (`Accept-Language`)
 *   4. `'en'` — final fallback
 */

import { loadTranslations } from '@angular/localize';

import { environment } from '../../../environments/environment';

export const SUPPORTED_LOCALES = ['ar', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const DEFAULT_LOCALE: SupportedLocale = 'en';

export function pickLocale(): SupportedLocale {
  // 1. URL ?locale=
  const url = new URL(window.location.href);
  const param = url.searchParams.get('locale');
  if (isSupported(param)) return param;

  // 2. cookie
  const cookieMatch = document.cookie.match(/(?:^|; )locale=([^;]+)/);
  const cookieValue = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
  if (isSupported(cookieValue)) return cookieValue;

  // 3. browser
  const accept = (navigator.language ?? '').slice(0, 2).toLowerCase();
  if (isSupported(accept)) return accept;

  // 4. fallback
  return DEFAULT_LOCALE;
}

export async function loadRuntimeTranslations(): Promise<void> {
  const locale = pickLocale();

  // Set document direction + lang for layout (RTL for AR, LTR for EN).
  // Pre-M.4, Angular's i18n compiler did this automatically based on
  // LOCALE_ID; with the single-bundle runtime, we have to do it manually
  // before any component renders so CSS logical properties resolve
  // correctly on first paint.
  applyDocumentDirection(locale);

  // Both locales fetch the runtime payload so that an admin editing a
  // Translation row in Django admin sees it reflected on the next load —
  // English included. English rows mirror the $localize source strings, so
  // applying them is a visual no-op until an admin overrides one; rows with an
  // empty msgstr are skipped server-side and fall back to the source string.
  // English used to early-return here, which silently dropped admin edits to
  // English copy (the whole point of the DB-backed i18n system).
  try {
    // Use the configured API base (absolute in dev → :8000, same-origin in
    // prod/staging → /api/v1) so the bootstrap fetch resolves the same way as
    // the rest of the app. A hardcoded relative path 404s under `ng serve`,
    // whose proxy does not forward /api.
    //
    // Default cache mode (no override) honours the endpoint's
    // `Cache-Control: max-age=30, must-revalidate` + ETag: served from cache
    // while fresh, then a conditional revalidation (304 when unchanged). The
    // previous `cache: 'force-cache'` was wrong — it serves a stale body
    // without ever revalidating, so admin edits never landed until the browser
    // evicted the entry.
    const response = await fetch(
      `${environment.apiBaseUrl}/core/i18n/messages/${locale}.json`,
    );
    if (!response.ok) return;
    const payload = (await response.json()) as Record<string, string>;
    loadTranslations(payload);
  } catch {
    // Silent fallback to source language. Never block bootstrap on i18n.
  }
}

/**
 * Set `<html dir>` and `<html lang>` to match the picked locale. Called
 * by the bootstrap before Angular boots so the very first render uses
 * the right direction. Idempotent — safe to call repeatedly.
 */
export function applyDocumentDirection(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.lang = locale;
  html.dir = locale === 'ar' ? 'rtl' : 'ltr';
}

function isSupported(value: string | null | undefined): value is SupportedLocale {
  return value !== null && value !== undefined && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
